import { Context } from '@/pipeline/stateMachine';
import gql from 'graphql-tag';
import { AnyEventObject, StateConfig } from 'xstate';
import { admissionsResultFields, GetAdmission } from '../queries/westerosQueries';

interface SendLoginCode {
  sent: boolean;
}

export interface SendLoginCodeMutationResult {
  sendLoginCode: SendLoginCode;
}

interface ValidateLoginCode {
  success: boolean;
  authToken: string;
}

interface CreateAuthToken {
  authToken: string;
}

export interface CreateAuthTokenMutationResult {
  createAuthToken: CreateAuthToken;
}

export interface ValidateLoginCodeMutationResult {
  validateLoginCode: ValidateLoginCode;
}

interface UpsertUser {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  signupMethod: string;
  accountId: number;
}

export interface UpsertUserMutationResult {
  upsertUser: UpsertUser;
}

export interface UpsertAdmissionMutationInput {
  courseId?: number;
  campusId?: number;
  admissionTypeId?: number;
  evaluationEssay?: {
    essayThemeId?: number;
    documentUrl?: string;
    userEssayTitle?: string;
    userEssayText?: string;
    hasAcceptedToS?: boolean;
  } | null;
  evaluationEnem?: {
    inscription?: string;
    year?: number;
    languagesGrade?: number;
    humanScienceGrade?: number;
    natureScienceGrade?: number;
    mathGrade?: number;
    essayGrade?: number;
    hasAcceptedToS?: boolean;
  } | null;
  evaluationSecondGraduation?: {
    institution?: string;
    courseName?: string;
    year?: string;
    documentUrl?: string;
    hasAcceptedToS?: boolean;
  } | null;
  evaluationTransfer?: {
    institution?: string;
    courseName?: string;
    period?: string;
    documentUrl?: string;
    hasAcceptedToS?: boolean;
  } | null;
}

interface UpsertAdmission extends GetAdmission {}

export interface UpsertAdmissionMutationResult {
  upsertAdmission: UpsertAdmission;
}

interface UpsertPipelineRun {
  id: number;
  state: StateConfig<Context, AnyEventObject>;
}

export interface UpsertPipelineRunMutationResult {
  upsertPipelineRun: UpsertPipelineRun;
}

export interface UploadEssayInput {
  admissionId: number;
  title?: string;
  text?: string;
  name?: string;
  dataUrl?: string;
}

interface TrackingEvent {
  eventName: string;
  eventData: { [key: string]: any };
}

export interface TrackingEventInput {
  data: TrackingEvent;
}

export const sendLoginCode = gql`
  mutation sendLoginCode($userId: Float!, $notificationChannel: NotificationChannel!) {
    sendLoginCode(userId: $userId, notificationChannel: $notificationChannel) {
      sent
    }
  }
`;

export const validateLoginCode = gql`
  mutation validateLoginCode($userId: Float!, $loginCode: String!) {
    validateLoginCode(userId: $userId, loginCode: $loginCode) {
      success
      authToken
    }
  }
`;

export const createAuthToken = gql`
  mutation createAuthToken($email: String!) {
    createAuthToken(email: $email) {
      authToken
    }
  }
`;

export const upsertUser = gql`
  mutation upsertUser(
    $fullName: String
    $email: String
    $phone: String
    $signupMethod: SignUpMethod
    $accountId: Float
  ) {
    upsertUser(
      fullName: $fullName
      email: $email
      phone: $phone
      signupMethod: $signupMethod
      accountId: $accountId
    ) {
      id
      fullName
      email
      phone
      signupMethod
      accountId
    }
  }
`;

export const upsertAdmission = gql`
  mutation upsertAdmission($data: UpsertAdmissionMutationInput!) {
    upsertAdmission(data: $data) {
      ${admissionsResultFields}
    }
  }
`;

export const upsertPipelineRun = gql`
  mutation upsertPipelineRun($admissionId: Float!, $state: JSON!) {
    upsertPipelineRun(admissionId: $admissionId, state: $state) {
      id
    }
  }
`;

export const uploadSecondGraduationDiploma = gql`
  mutation uploadSecondGraduationDiploma(
    $admissionId: Float!
    $fileName: String!
    $fileDataUrl: String!
    $side: DocumentSide!
  ) {
    uploadSecondGraduationDiploma(
      admissionId: $admissionId
      fileName: $fileName
      documentDataUrl: $fileDataUrl
      side: $side
    )
  }
`;

export const uploadTransferTranscript = gql`
  mutation uploadTransferTranscript(
    $admissionId: Float!
    $fileName: String!
    $fileDataUrl: String!
  ) {
    uploadTransferTranscript(
      admissionId: $admissionId
      fileName: $fileName
      documentDataUrl: $fileDataUrl
    )
  }
`;

export const uploadEssay = gql`
  mutation uploadEssay($data: UploadEssayInput!) {
    uploadEssay(data: $data)
  }
`;

export const trackEvent = gql`
  mutation trackEvent($data: TrackingEventInput!) {
    trackEvent(data: $data) {
      success
    }
  }
`;
